/**
 * Global Error Reporter
 *
 * Bridges errors from outside React (global handlers, async code)
 * into the React error reporting system.
 *
 * Usage:
 * - Call `registerErrorReporter(fn)` when ErrorReporterProvider mounts
 * - Call `reportGlobalError(error)` from anywhere to trigger the dialog
 * - Errors before React mounts are queued and replayed automatically
 */

type ReportErrorFn = (error: Error, componentStack?: string) => void

let registeredReporter: ReportErrorFn | null = null
const pendingErrors: Array<{ error: Error; stack?: string }> = []

/**
 * Register the React error reporter function.
 * Called by ErrorReporterProvider on mount.
 */
export function registerErrorReporter(reporter: ReportErrorFn): () => void {
	registeredReporter = reporter

	// Replay any errors that occurred before React mounted
	if (pendingErrors.length > 0) {
		const errorToReport = pendingErrors.shift()
		if (errorToReport) {
			reporter(errorToReport.error, errorToReport.stack)
		}
		// Clear remaining - only show one at a time
		pendingErrors.length = 0
	}

	// Return cleanup function
	return () => {
		registeredReporter = null
	}
}

/**
 * Capture the current call stack at the point of error capture.
 * Useful when the original error (e.g. SyntaxError from JSON.parse)
 * has a useless stack trace that doesn't show which code path triggered it.
 */
function captureCallStack(): string {
	const captureError = new Error('__capture__')
	const stack = captureError.stack ?? ''
	// Strip the first 3 lines: "Error: __capture__", this function, and the caller
	return stack.split('\n').slice(3).join('\n')
}

/**
 * Enrich an error's stack trace with the capture-site call stack.
 * If the original error has a meaningful stack, appends the capture context.
 * If the original stack is just the message (common for SyntaxError), replaces it.
 */
function enrichErrorStack(error: Error, capturedStack: string): void {
	const originalStack = error.stack ?? ''
	const stackLines = originalStack.split('\n')
	// SyntaxError and similar often have stack = just the message with no "at" frames
	const hasCallFrames = stackLines.some(line => line.trimStart().startsWith('at '))

	if (hasCallFrames) {
		error.stack = `${originalStack}\n    --- captured at ---\n${capturedStack}`
	} else {
		error.stack = `${error.name}: ${error.message}\n    --- captured at ---\n${capturedStack}`
	}
}

/**
 * Report an error from anywhere in the app.
 * If React isn't ready, queues the error for later.
 */
export function reportGlobalError(error: Error, componentStack?: string): void {
	// Don't report the same error multiple times in quick succession
	if (pendingErrors.some((e) => e.error.message === error.message)) {
		return
	}

	if (registeredReporter) {
		registeredReporter(error, componentStack)
	} else {
		// Queue for when React mounts
		pendingErrors.push({ error, stack: componentStack })
	}
}

/**
 * Check if an error should be reported.
 * Filters out noise like ResizeObserver errors.
 */
function shouldReportError(error: Error | string): boolean {
	const message = typeof error === 'string' ? error : error.message

	// Filter out common browser noise
	const ignoredPatterns = [
		'ResizeObserver loop',
		'Script error.',
		'Loading chunk',
		'Loading CSS chunk',
		'NetworkError',
		'Failed to fetch',
		'Load failed',
		'cancelled',
	]

	return !ignoredPatterns.some((pattern) => message.includes(pattern))
}

/**
 * Setup global error handlers.
 * Call this once at app startup (in frontend.tsx).
 */
export function setupGlobalErrorHandlers(): void {
	// Override console.error to prevent Bun's dev overlay while still logging
	const originalConsoleError = console.error
	console.error = (...args: unknown[]) => {
		originalConsoleError(...args)
		const capturedStack = captureCallStack()
		const errorArg = args.find((arg): arg is Error => arg instanceof Error)
		if (errorArg && shouldReportError(errorArg)) {
			enrichErrorStack(errorArg, capturedStack)
			reportGlobalError(errorArg)
		}
	}

	// Handle uncaught errors
	window.onerror = (message, source, lineno, colno, error) => {
		// Create error if not provided
		const errorObj =
			error ??
			new Error(typeof message === 'string' ? message : 'Unknown error')

		if (shouldReportError(errorObj)) {
			// Build full stack: original error stack + source location
			const sourceLocation = source ? `    at ${source}:${lineno}:${colno}` : ''
			if (sourceLocation) {
				enrichErrorStack(errorObj, sourceLocation)
			}

			// Always log to console for immediate visibility
			originalConsoleError('[PlaySmith Error]', errorObj.message, '\n', errorObj.stack)

			reportGlobalError(errorObj)
			// Return true to prevent default error handling (Bun's overlay)
			// since we're already showing our custom ErrorReportDialog
			return true
		}

		// Allow default handling for ignored errors
		return false
	}

	// Handle unhandled promise rejections
	window.onunhandledrejection = (event) => {
		const capturedStack = captureCallStack()
		const error =
			event.reason instanceof Error
				? event.reason
				: new Error(String(event.reason ?? 'Unhandled promise rejection'))

		if (shouldReportError(error)) {
			// Enrich with the call stack captured at rejection time
			enrichErrorStack(error, capturedStack)

			// Always log to console for immediate visibility
			originalConsoleError('[PlaySmith Unhandled Rejection]', error.message, '\n', error.stack)

			reportGlobalError(error)
			// Prevent default to avoid showing Bun's dev error overlay
			// since we're already showing our custom ErrorReportDialog
			event.preventDefault()
		}
	}
}
